import { useCommunityVisionAi } from '@/app/community/composables/useCommunityVisionAi';
import { useDashboardsVisionAi } from '@/app/dashboards/composables/useDashboardsVisionAi';
import { useListeningVisionAi } from '@/app/socialListening/composables/useListeningVisionAi';
import {
  COMMUNITY_VISION_AI_TOOLTIPS,
  DASHBOARD_SUMMARIZE_BUTTON_TOOLTIP,
  DASHBOARD_SUMMARY_NO_DASHBOARDS_MESSAGE,
  DEFAULT_METRIC_AND_ANALYTICS_PROMPTS_TOOLTIP,
  FORMATTED_DASHBOARD_SUMMARY_REPORT_MINIMUM_MESSAGE,
  INTENT_TYPES,
  REQUEST_CONTEXT_BUTTON_IDENTIFIERS,
  SOCIAL_LISTENING_VISION_AI_TOOLTIPS,
} from '@/components/VisionAi/constants';

export function useIntentMap() {
  const { hasValidCommentsThreshold, hasValidDmsThreshold, isCommunityEmptyState } =
    useCommunityVisionAi();
  const {
    hasValidSocialListeningTrendsThreshold,
    hasValidSocialListeningTopicsThreshold,
    isListeningEmptyState,
  } = useListeningVisionAi();
  const { hasNoDashboards } = useDashboardsVisionAi();

  function checkIfInvalidThreshold(summaryType) {
    switch (summaryType) {
      case INTENT_TYPES.COMMENTS:
      case REQUEST_CONTEXT_BUTTON_IDENTIFIERS.SUMMARIZE_COMMENT_THREAD:
        return !hasValidCommentsThreshold.value;
      case INTENT_TYPES.DMS:
      case REQUEST_CONTEXT_BUTTON_IDENTIFIERS.SUMMARIZE_DM_THREAD:
        return !hasValidDmsThreshold.value;
      case INTENT_TYPES.TOPICS:
        return !hasValidSocialListeningTopicsThreshold.value;
      case INTENT_TYPES.TRENDS:
        return !hasValidSocialListeningTrendsThreshold.value;
      case INTENT_TYPES.DASHBOARD_REPORTS:
        return hasNoDashboards.value;
      case INTENT_TYPES.UNIFIED:
      case REQUEST_CONTEXT_BUTTON_IDENTIFIERS.SUMMARIZE_COMMUNITY_INSIGHTS:
      case REQUEST_CONTEXT_BUTTON_IDENTIFIERS.SUMMARIZE_COMMUNITY_MESSAGES:
      case REQUEST_CONTEXT_BUTTON_IDENTIFIERS.SUMMARIZE_CAMPAIGN_COMMENTS:
        return false;
      default:
        return true;
    }
  }

  function defaultTooltip() {
    if (isCommunityEmptyState.value) {
      return COMMUNITY_VISION_AI_TOOLTIPS.EMPTY_STATE.DISABLED;
    }

    if (isListeningEmptyState.value) {
      return SOCIAL_LISTENING_VISION_AI_TOOLTIPS.EMPTY_STATE.DISABLED;
    }
    return null;
  }

  function getTooltip(intentType) {
    switch (intentType) {
      case INTENT_TYPES.COMMENTS:
        return COMMUNITY_VISION_AI_TOOLTIPS.COMMENTS.ENABLED;
      case INTENT_TYPES.DMS:
        return COMMUNITY_VISION_AI_TOOLTIPS.DMS.ENABLED;
      case INTENT_TYPES.TOPICS:
        return SOCIAL_LISTENING_VISION_AI_TOOLTIPS.TOPICS.ENABLED;
      case INTENT_TYPES.TRENDS:
        return SOCIAL_LISTENING_VISION_AI_TOOLTIPS.TRENDS.ENABLED;
      case INTENT_TYPES.DASHBOARD_REPORTS:
        return DASHBOARD_SUMMARIZE_BUTTON_TOOLTIP;
      case INTENT_TYPES.METRIC_ANALYTICS:
        return DEFAULT_METRIC_AND_ANALYTICS_PROMPTS_TOOLTIP;
      default:
        return null;
    }
  }

  function getWarningMessage(intentType) {
    switch (intentType) {
      case INTENT_TYPES.COMMENTS:
        return COMMUNITY_VISION_AI_TOOLTIPS.COMMENTS.DISABLED;
      case INTENT_TYPES.DMS:
        return COMMUNITY_VISION_AI_TOOLTIPS.DMS.DISABLED;
      case INTENT_TYPES.TOPICS:
        return SOCIAL_LISTENING_VISION_AI_TOOLTIPS.TOPICS.DISABLED;
      case INTENT_TYPES.TRENDS:
        return SOCIAL_LISTENING_VISION_AI_TOOLTIPS.TRENDS.DISABLED;
      case INTENT_TYPES.DASHBOARD_REPORTS:
        if (hasNoDashboards.value) {
          return DASHBOARD_SUMMARY_NO_DASHBOARDS_MESSAGE;
        }
        return FORMATTED_DASHBOARD_SUMMARY_REPORT_MINIMUM_MESSAGE;

      default:
        return defaultTooltip();
    }
  }

  return {
    checkIfInvalidThreshold,
    getTooltip,
    getWarningMessage,
  };
}
